/**
 * UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2) -- the ONE page-header
 * primitive.
 *
 * The "page header" experience rule: every surface opens with the SAME header
 * grammar -- optional breadcrumb trail, one h1 title, optional eyebrow, zero+
 * subtitle/context lines, and an optional right-aligned actions cluster. It
 * formalises the `.page-header` CSS idiom already used across ~30 surfaces
 * (frontend/src/dashboard/dashboard.css `.page-header` block) into ONE owner so
 * new surfaces inherit the header instead of re-rolling it. Adopted in this
 * package by the four representative role homes + the shell; other surfaces keep
 * the raw idiom and adopt later (no repo-wide migration).
 *
 * Generic by construction (the PORTABILITY TEST): it carries NO business
 * derivation -- title/subs/actions are caller-supplied nodes -- so Lead /
 * Patient / Appointment / Visit / Treatment / Payment / Case Sheet headers all
 * reuse it. It reuses the existing `--ac-*` tokens via the `.page-header`
 * classes; it embeds no data fetching and no mutation.
 */

import type { ReactNode } from 'react';
import Breadcrumbs, { type BreadcrumbItem } from './Breadcrumbs';
import './signatures.css';

export interface PageHeaderProps {
  readonly title: ReactNode;
  /** Breadcrumb trail rendered above the title. Omit for a top-level home. */
  readonly breadcrumbs?: readonly BreadcrumbItem[];
  /** Small uppercase label above the title (e.g. the role / section name). */
  readonly eyebrow?: ReactNode;
  /**
   * Subtitle / context lines rendered under the title as `.page-header__sub`.
   * A node may carry its own attributes (e.g. a testid span) -- the wrapper
   * preserves it. Falsy entries are skipped so callers can spread conditionals.
   */
  readonly subs?: ReadonlyArray<ReactNode>;
  /** Right-aligned actions cluster (one primary action + secondaries). */
  readonly actions?: ReactNode;
  readonly testId?: string;
  /* -- S5 HEADER + SCOPE (PLATFORM-DESIGN-SYSTEM-CODIFICATION-V1;
     owner-home/SYSTEM-EXTRACT.md S5 + conformance C-9) -- ADDITIVE: the
     one masthead grammar gains the scope/date/honesty slots; existing
     adopters render unchanged when these are omitted. The LAW the slots
     carry (proven per surface, not here): every money figure prints its
     scope AND window; partial windows draw values only, no delta. */
  /** SCOPE control (clinic / all clinics chip). */
  readonly scope?: ReactNode;
  /** DATE + day pager slot (the DateCommandBar grammar, IST). */
  readonly dateControl?: ReactNode;
  /** The data-honesty badge slot (e.g. SYNTHETIC / test-org marker). */
  readonly honestyBadge?: ReactNode;
}

export default function PageHeader({
  title,
  breadcrumbs,
  eyebrow,
  subs,
  actions,
  testId,
  scope,
  dateControl,
  honestyBadge,
}: PageHeaderProps): JSX.Element {
  const subLines = (subs ?? []).filter((s): s is ReactNode => Boolean(s));
  const hasContext = Boolean(scope || dateControl || honestyBadge);
  return (
    <header className="page-header" data-testid={testId}>
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <Breadcrumbs items={breadcrumbs} />
      ) : null}
      <div className="page-header__bar">
        <div className="page-header__heading">
          {eyebrow ? (
            <span className="page-header__eyebrow">{eyebrow}</span>
          ) : null}
          <h1>{title}</h1>
          {subLines.map((sub, index) => (
            <p className="page-header__sub" key={index}>
              {sub}
            </p>
          ))}
        </div>
        {actions ? (
          <div className="page-header__actions">{actions}</div>
        ) : null}
      </div>
      {hasContext ? (
        <div className="page-header__context" data-testid="page-header-context">
          {scope ?? null}
          {dateControl ?? null}
          {honestyBadge ?? null}
        </div>
      ) : null}
    </header>
  );
}
